'use client'

import { clientOnlyDynamic } from '@/lib/client-only-dynamic'
import { forwardRef, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { formatDate, getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import {
  mastheadDefaultSrc,
  mastheadDesktopSrcSet,
  mastheadMobileSrcSet,
} from '@/lib/cdn-srcset'
import { rewriteContentImageUrlsForDisplay } from '@/lib/process-article-content-images'
import { linkArticleTagsInHtml } from '@/lib/link-article-tags-in-html'
import { useArticleTweetEmbeds } from '@/lib/use-article-tweet-embeds'
import { useNativeProviderEmbeds } from '@/lib/use-native-provider-embeds'
import PhotoGallery from '@/components/PhotoGallery'
import AdSlotWrapper from '@/components/AdSlotWrapper'
import CommentThreadWrapper from '@/components/comments/CommentThreadWrapper'
import ArticleAdminEditLink from '@/components/ArticleAdminEditLink'
import LiveBlogUpdates from '@/components/LiveBlogUpdates'
import LiveBadge from '@/components/LiveBadge'
import GooglePreferredSourceBanner from '@/components/GooglePreferredSourceBanner'
import ArticleInlineTagHover from '@/components/article/ArticleInlineTagHover'
import type { TagInfoCardData } from '@/components/tag/TagInfoCard'
import { getApiBaseUrl } from '@/lib/config'
import { absoluteUrl } from '@/lib/site-seo'
import { hasVideoSource, isClassicVideoNews, isShortNews } from '@/lib/news-types'
import { getPublicSiteConfig } from '@/lib/public-site-config'
import {
  formatEditorDisplayName,
  getAuthorAvatarUrl,
  getAuthorInitial,
  getAuthorProfileHref,
} from '@/lib/author-url'
import { sanitizePublicArticleHtml } from '@/lib/safe-html'
import ArticleShareSocial from '@/components/ArticleShareSocial'

function hasUpdatedDate(createdAt: Date | string, updatedAt?: Date | string | null) {
  if (!updatedAt) return false
  const created = new Date(createdAt).getTime()
  const updated = new Date(updatedAt).getTime()
  if (!Number.isFinite(created) || !Number.isFinite(updated)) return false
  return Math.abs(updated - created) > 60_000
}

const subscribeNativeShare = () => () => {}
const getNativeShareSnapshot = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function'
const getServerNativeShareSnapshot = () => false

const AccessibilityToolbar = clientOnlyDynamic(
  () => import('@/components/AccessibilityToolbar'),
  { loading: () => null },
)

const VideoPlayer = clientOnlyDynamic(() => import('@/components/VideoPlayer'), { loading: () => <div className="relative mb-2 aspect-video w-full animate-pulse rounded-lg bg-black/80 sm:mb-2.5" />,
})

const ShortVideoPlayer = clientOnlyDynamic(() => import('@/components/shorts/ShortVideoPlayer'), { loading: () => (
    <div className="relative mx-auto mb-2 aspect-[9/16] w-full max-w-sm animate-pulse rounded-lg bg-black/80 sm:mb-2.5" />
  ),
})

export interface ArticleNews {
  id: number
  title: string
  titleSub?: string | null
  description?: string | null
  slug: string
  image: string | null
  content: string | null
  createdAt: Date | string
  updatedAt?: Date | string | null
  commentCount?: number | null
  comments?: number | null
  live?: number | null
  viewCount?: number | null
  shareCount?: number | null
  video?: string | null
  youtube?: string | null
  dailymotion?: string | null
  vimeo?: string | null
  x?: string | null
  facebook?: string | null
  playerType?: number | null
  isType?: number | null
  category: {
    id: number
    title: string
    slug: string
  } | null
  editor?: {
    id: number
    name: string
    surname?: string | null
    username?: string | null
    avatar?: string | null
    image?: string | null
  } | null
  editorId?: number | null
  authorId?: number | null
  tags?: Array<{
    id: number
    tag: {
      id: number
      tag: string
      slug: string
      tagUrl?: string | null
      tag_image?: string | null
      tag_content?: string | null
      tagImage?: string | null
      tagContent?: string | null
    }
  }>
  /** Habere bağlı + harici URL etiketleri (içerik linkleme) */
  contentLinkTags?: Array<{
    tag: string
    slug: string
    tagUrl?: string | null
    tagImage?: string | null
    tagContent?: string | null
    tag_image?: string | null
    tag_content?: string | null
  }>
  photos?: Array<{
    id: number
    image: string
    imageSort: number | null
    title?: string | null
    titleSub?: string | null
  }>
}

interface ArticleDetailProps {
  news: ArticleNews
  categorySlug: string
  slugString: string
  newsImageUrl?: string | null
  /** SSR’de CDN ile çözülmüş içerik HTML */
  contentHtml?: string | null
  articleId?: string
  imagePriority?: boolean
  showBreadcrumb?: boolean
  showComments?: boolean
  /** Masthead_Top — sonsuz kaydırma haberlerinde (haber.com) */
  showMastheadTopAd?: boolean
  className?: string
  liveUpdates?: Array<{ id: number; body: string; createdAt: string }>
  /** SSR’de önceden çözümlenmiş inline etiket bilgileri (ⓘ popover) */
  preloadedTagInfos?: Record<string, TagInfoCardData>
}

const ArticleDetail = forwardRef<HTMLElement, ArticleDetailProps>(function ArticleDetail(
  {
    news,
    categorySlug,
    slugString,
    newsImageUrl,
    contentHtml,
    articleId,
    imagePriority = false,
    showBreadcrumb = true,
    showComments = true,
    showMastheadTopAd = true,
    className = '',
    liveUpdates = [],
    preloadedTagInfos,
  },
  ref,
) {
  const catSlug = news.category?.slug || categorySlug
  const shareUrl = absoluteUrl(`/${catSlug}/${slugString}`)
  const editorName = formatEditorDisplayName(news.editor) || 'Haber.com'
  const editorHref = getAuthorProfileHref(news.editor)
  const editorAvatarRaw = getAuthorAvatarUrl(news.editor)
  const editorAvatar = editorAvatarRaw
    ? getImageUrl(editorAvatarRaw, IMAGE_PRESETS.avatar)
    : null
  const showUpdated = hasUpdatedDate(news.createdAt, news.updatedAt)
  const commentsId = `comments-${news.id}`
  const commentsOpen = showComments && Number(news.comments ?? 1) !== 0
  const [commentCountOverride, setCommentCountOverride] = useState<{
    newsId: number
    count: number
  } | null>(null)
  const displayCommentCount =
    commentCountOverride?.newsId === news.id ? commentCountOverride.count : (news.commentCount ?? 0)
  const [linkCopied, setLinkCopied] = useState(false)
  const canNativeShare = useSyncExternalStore(
    subscribeNativeShare,
    getNativeShareSnapshot,
    getServerNativeShareSnapshot,
  )
  const [articleVideoAutoplay, setArticleVideoAutoplay] = useState(false)
  const [articleVideoInitialVolume, setArticleVideoInitialVolume] = useState(50)
  const [articleVideoConfigReady, setArticleVideoConfigReady] = useState(false)
  const [googlePreferredSourceEnabled, setGooglePreferredSourceEnabled] = useState(false)

  useEffect(() => {
    let cancelled = false
    void getPublicSiteConfig().then((config) => {
      if (!cancelled) {
        setArticleVideoAutoplay(config.article_video_autoplay === '1')
        const configuredVolume = Number(config.article_video_initial_volume)
        setArticleVideoInitialVolume(
          Number.isFinite(configuredVolume) ? Math.min(100, Math.max(0, Math.round(configuredVolume))) : 50,
        )
        setGooglePreferredSourceEnabled(config.google_preferred_source_enabled !== '0')
        setArticleVideoConfigReady(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const trackShare = () => {
    void fetch(`${getApiBaseUrl()}/api/news/${news.id}/share`, {
      method: 'GET',
      keepalive: true,
    }).catch(() => {})
  }

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setLinkCopied(true)
      trackShare()
      window.setTimeout(() => setLinkCopied(false), 2000)
    } catch {
      /* ignore */
    }
  }

  const nativeShare = async () => {
    if (typeof navigator === 'undefined' || !navigator.share) return
    try {
      await navigator.share({ title: news.title, url: shareUrl })
      trackShare()
    } catch {
      /* kullanıcı iptal */
    }
  }

  const isShort = isShortNews(news.isType)
  const isVideoNews = (isClassicVideoNews(news.isType) || isShort) && hasVideoSource(news)

  const isVideoNewsWithoutSource = (isClassicVideoNews(news.isType) || isShort) && !isVideoNews

  const isPhotoGallery = news.isType === 1

  const heroImageUrl =
    newsImageUrl ?? (news.image ? getImageUrl(news.image, IMAGE_PRESETS.sectionLead) : null)

  const contentRef = useRef<HTMLDivElement>(null)

  const inlineTagContainerSelector =
    articleId === 'main-article' ? '#main-article .news-content' : `article[data-article-id="${news.id}"] .news-content`

  const renderedContent = useMemo(() => {
    const base = contentHtml?.trim()
      ? contentHtml
      : rewriteContentImageUrlsForDisplay(news.content || '')
    // SSR zaten linklediyse (contentHtml) tekrar işleme — çift etiketlenmesin
    let html = base
    if (!contentHtml?.trim()) {
      const linkSource =
        news.contentLinkTags ||
        (news.tags || []).map((row) => ({
          tag: row.tag?.tag || '',
          slug: row.tag?.slug || '',
          tagUrl: row.tag?.tagUrl || null,
          tagImage: row.tag?.tagImage ?? row.tag?.tag_image ?? null,
          tagContent: row.tag?.tagContent ?? row.tag?.tag_content ?? null,
        }))
      const tagRefs = linkSource
        .map((row) => ({
          name: row.tag || '',
          slug: row.slug || '',
          url: row.tagUrl || null,
          hasInfo: Boolean(String(row.tagImage ?? '').trim() || String(row.tagContent ?? '').trim()),
        }))
        .filter((t) => t.name && t.slug)
        .sort((a, b) => b.name.length - a.name.length)
      html = linkArticleTagsInHtml(base, tagRefs)
    }
    // Strip meta refresh / script / event handlers even for legacy DB content
    return sanitizePublicArticleHtml(html)
  }, [contentHtml, news.content, news.tags, news.contentLinkTags])

  useArticleTweetEmbeds(contentRef, renderedContent)
  useNativeProviderEmbeds(contentRef, renderedContent)

  return (
    <>
      {showBreadcrumb && (
        <nav className="mb-2 text-xs breadcrumb-nav sm:mb-3 sm:text-sm" aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 sm:gap-x-2">
            <li>
              <Link href="/" className="hover:text-red-500 transition-colors breadcrumb-link">
                Ana Sayfa
              </Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li>
              <Link href={`/${catSlug}`} className="hover:text-red-500 transition-colors breadcrumb-link">
                {news.category?.title || 'Haber'}
              </Link>
            </li>
          </ol>
        </nav>
      )}

      <article ref={ref} id={articleId} data-article-id={news.id} className={className}>
        <div className="mb-1.5 flex flex-wrap items-start gap-2 sm:mb-2 sm:gap-3">
          <h1 className="news-detail-title mb-0 min-w-0 flex-1">{news.title}</h1>
          {Number(news.live) === 1 || (liveUpdates?.length ?? 0) > 0 ? <LiveBadge variant="inline" /> : null}
          <ArticleAdminEditLink
            articleId={news.id}
            authorId={news.authorId}
            editorId={news.editorId ?? news.editor?.id}
          />
        </div>

        {(news.description || news.titleSub) && (
          <p className="news-detail-summary">{news.description || news.titleSub}</p>
        )}

        <div className="flex min-w-0 max-w-full flex-col gap-2 pb-1.5 text-xs sm:gap-2 sm:pb-2 sm:text-sm md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 sm:gap-x-3 sm:gap-y-1.5 md:gap-4">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 sm:gap-x-3">
              <div className="flex min-w-0 items-center gap-1">
                <svg
                  className="h-4 w-4 shrink-0 text-gray-600 dark:text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <time
                  dateTime={new Date(news.createdAt).toISOString()}
                  className="whitespace-nowrap text-gray-700 dark:text-slate-300"
                  title="Yayın tarihi"
                >
                  {formatDate(news.createdAt)}
                </time>
              </div>
              {showUpdated && news.updatedAt ? (
                <div className="flex min-w-0 items-center gap-1">
                  <svg
                    className="h-4 w-4 shrink-0 text-gray-600 dark:text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <time
                    dateTime={new Date(news.updatedAt).toISOString()}
                    className="whitespace-nowrap text-gray-700 dark:text-slate-300"
                    title="Güncelleme tarihi"
                  >
                    {formatDate(news.updatedAt)}
                  </time>
                </div>
              ) : null}
            </div>
            {editorName ? (
              <div className="flex min-w-0 items-center gap-2">
                {editorAvatar ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={editorAvatar}
                    alt=""
                    className="h-5 w-5 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-600 dark:bg-slate-700 dark:text-gray-300">
                    {getAuthorInitial(news.editor)}
                  </span>
                )}
                {editorHref ? (
                  <Link href={editorHref} className="truncate hover:text-red-600 dark:hover:text-red-400">
                    {editorName}
                  </Link>
                ) : (
                  <span className="truncate">{editorName}</span>
                )}
              </div>
            ) : null}
          </div>

          <ArticleShareSocial
            title={news.title}
            shareUrl={shareUrl}
            commentsId={commentsId}
            commentsOpen={commentsOpen}
            commentCount={displayCommentCount}
            canNativeShare={canNativeShare}
            linkCopied={linkCopied}
            onTrackShare={trackShare}
            onCopyLink={() => void copyShareLink()}
            onNativeShare={() => void nativeShare()}
          />
        </div>

        {isVideoNews && isShort ? (
          <div className="relative mx-auto mb-4 w-full max-w-sm overflow-hidden rounded-xl bg-black sm:mb-5">
            <div className="aspect-[9/16] w-full">
              <ShortVideoPlayer
                video={news.video}
                youtube={news.youtube}
                dailymotion={news.dailymotion}
                vimeo={news.vimeo}
                x={news.x}
                facebook={news.facebook}
                playerType={news.playerType}
                isActive
                playing
                muted={false}
              />
            </div>
            <p className="px-3 py-2 text-center text-xs text-gray-500 dark:text-slate-400">
              <Link href="/shorts" className="text-[#D92E2E] hover:underline">
                Tüm Shorts videolarını izle
              </Link>
            </p>
          </div>
        ) : isVideoNews && !articleVideoConfigReady ? (
          <div
            className="relative mb-2 aspect-video w-full animate-pulse rounded-lg bg-black/90 sm:mb-2.5"
            aria-label="Video hazırlanıyor"
            aria-busy="true"
          />
        ) : isVideoNews ? (
          <VideoPlayer
            video={news.video}
            youtube={news.youtube}
            dailymotion={news.dailymotion}
            vimeo={news.vimeo}
            x={news.x}
            facebook={news.facebook}
            playerType={news.playerType}
            image={news.image}
            title={news.title}
            autoPlay={articleVideoAutoplay}
            initialVolume={articleVideoInitialVolume}
          />
        ) : isVideoNewsWithoutSource ? (
          <div className="relative mb-2 flex aspect-video w-full items-center justify-center rounded-lg bg-neutral-900 text-sm text-neutral-300 sm:mb-2.5">
            Video kaldırıldı
          </div>
        ) : isPhotoGallery && news.photos && news.photos.length > 0 ? (
          <PhotoGallery
            photos={news.photos}
            newsTitle={news.title}
            newsId={news.id}
            categorySlug={catSlug}
            newsSlug={news.slug}
            content={news.content}
            newsDescription={news.description}
          />
        ) : !isPhotoGallery && heroImageUrl && heroImageUrl.trim() !== '' ? (
          <div className="relative mb-4 aspect-video w-full overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-900 sm:mb-5">
            {imagePriority ? (
              <picture>
                <source
                  media="(max-width: 767px)"
                  srcSet={mastheadMobileSrcSet(heroImageUrl)}
                  sizes="100vw"
                />
                <source
                  media="(min-width: 768px)"
                  srcSet={mastheadDesktopSrcSet(heroImageUrl)}
                  sizes="(max-width: 1024px) 100vw, 896px"
                />
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={mastheadDefaultSrc(heroImageUrl)}
                  alt={news.title}
                  className="absolute inset-0 h-full w-full object-cover object-center"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            ) : (
              <Image
                src={heroImageUrl}
                alt={news.title}
                fill
                className="object-cover object-center"
                loading="lazy"
                sizes="(max-width: 768px) 100vw, 896px"
              />
            )}
          </div>
        ) : null}

        {/* Banner hero sonrası — boşken yer yok (margin AdSlot doluyken) */}
        {!isPhotoGallery && (
          <div className="min-w-0 max-w-full overflow-x-clip">
            <AdSlotWrapper slot="article-banner" format="horizontal" lazy />
          </div>
        )}

        {Number(news.live) === 1 || (liveUpdates?.length ?? 0) > 0 ? (
          <LiveBlogUpdates newsId={news.id} initialUpdates={liveUpdates} />
        ) : null}

        {!isPhotoGallery && <AccessibilityToolbar newsId={news.id} title={news.title} content={news.content || ''} />}

        {!isPhotoGallery && googlePreferredSourceEnabled && <GooglePreferredSourceBanner />}

        {!isPhotoGallery && showMastheadTopAd && (
          <div className="min-w-0 max-w-full overflow-x-clip">
            <AdSlotWrapper slot="article-top" slotId={`article-top-${news.id}`} format="horizontal" lazy />
          </div>
        )}
        {!isPhotoGallery && (
          <div className="news-content-outer mb-8">
            <div ref={contentRef} className="news-content" dangerouslySetInnerHTML={{ __html: renderedContent }} />
            <ArticleInlineTagHover
              containerSelector={inlineTagContainerSelector}
              preloadedTagInfos={preloadedTagInfos}
            />
          </div>
        )}

        {!isPhotoGallery && (
          <div className="min-w-0 max-w-full overflow-x-clip">
            <AdSlotWrapper slot="article-bottom" slotId={`article-bottom-${news.id}`} format="square" lazy />
          </div>
        )}

        {!isPhotoGallery && news.photos && news.photos.length > 0 && (
          <PhotoGallery
            photos={news.photos}
            newsTitle={news.title}
            newsId={news.id}
            categorySlug={catSlug}
            newsSlug={news.slug}
            content={news.content}
            newsDescription={news.description}
          />
        )}

        {news.tags && news.tags.length > 0 && (
          <div className="mb-8">
            <h2 className="mb-2 text-base font-semibold text-gray-900 sm:mb-3 sm:text-lg dark:text-gray-100">
              Etiketler
            </h2>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {news.tags.map((tagNews, index) => (
                <Link
                  key={tagNews.id || tagNews.tag?.id || tagNews.tag?.slug || `tag-${index}`}
                  href={`/haberleri/${tagNews.tag?.slug || 'haber'}`}
                  className="rounded-full bg-gray-700 px-3 py-1.5 text-xs text-white transition-colors hover:bg-red-600 sm:px-4 sm:py-2 sm:text-sm dark:bg-slate-700"
                >
                  #{tagNews.tag?.tag || 'etiket'}
                </Link>
              ))}
            </div>
          </div>
        )}

        {commentsOpen ? (
          <div
            id={commentsId}
            className="mt-8 min-w-0 max-w-full scroll-mt-28 overflow-x-clip border-t border-gray-200 pt-5 dark:border-slate-700 sm:mt-10 sm:pt-6 md:mt-12 md:scroll-mt-36 md:pt-8"
          >
            <CommentThreadWrapper
              targetType="article"
              targetId={news.id}
              onTotalCountChange={(count) => {
                setCommentCountOverride({ newsId: news.id, count })
              }}
            />
          </div>
        ) : null}
      </article>
    </>
  )
})

export default ArticleDetail
